import React, { useState, useEffect } from 'react';
import { RefreshCw, X, Sparkles } from 'lucide-react';
import { useThemeStore } from '@/stores';
import { APP_VERSION } from '../../version';

export const UpdateNotifier: React.FC = () => {
  const { theme } = useThemeStore();
  const isLight = theme === 'light';
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [newVersion, setNewVersion] = useState<string | null>(null);

  const checkVersion = async () => {
    // Disable update notifier in local dev environment
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      return;
    }

    try {
      const url = `${window.location.origin}/version.json?t=${Date.now()}`;
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.version && data.version !== APP_VERSION) {
          // Prevent update loop if this version was already applied in current session
          const lastApplied = sessionStorage.getItem('last_applied_version');
          if (lastApplied === data.version) {
            return;
          }
          console.log(`[Version Check] New version detected: ${data.version} (current: ${APP_VERSION})`);
          setNewVersion(data.version);
          setHasUpdate(true);
        }
      }
    } catch (err) {
      console.warn('[Version Check] Failed to check for updates:', err);
    }
  };

  useEffect(() => {
    // Check on mount
    checkVersion();

    // Check periodically every 30 seconds
    const interval = setInterval(checkVersion, 30000);

    // Check on window focus (whenever PWA is resumed/brought to foreground)
    const handleFocus = () => {
      checkVersion();
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      console.log('[Update] Non-destructive update: updating service workers and reloading...');
      
      if (newVersion) {
        sessionStorage.setItem('last_applied_version', newVersion);
      }

      // 1. Update Service Workers non-destructively (never unregister OneSignal or PWA workers)
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.update().catch(() => {});
        }
      }

      // 2. Perform clean reload without query mangling or session storage wipe
      window.location.reload();
    } catch (err) {
      console.error('[Update] Error during update reload:', err);
      window.location.reload();
    }
  };

  if (!hasUpdate) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '24px',
        right: '24px',
        maxWidth: '500px',
        margin: '0 auto',
        zIndex: 11000, // Higher than header and sidebar
        background: isLight 
          ? 'linear-gradient(135deg, #ffffff, #f0fdf4)' 
          : 'linear-gradient(135deg, #0a1f16, #050f0b)',
        border: isLight 
          ? '1px solid #10b981' 
          : '1px solid rgba(16, 185, 129, 0.4)',
        borderRadius: '16px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
        padding: '18px 22px',
        animation: 'updateSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        direction: 'rtl',
      }}
    >
      <style>{`
        @keyframes updateSlideUp {
          from { transform: translateY(100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      
      {/* Close button */}
      <button
        onClick={() => setHasUpdate(false)}
        style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          background: 'transparent',
          border: 'none',
          color: isLight ? '#047857' : '#34d399',
          cursor: 'pointer',
          padding: '4px',
        }}
      >
        <X size={18} />
      </button>

      {/* Content */}
      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
        {/* Icon */}
        <div
          style={{
            background: 'rgba(16, 185, 129, 0.15)',
            borderRadius: '12px',
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#10b981',
            flexShrink: 0,
            marginTop: '2px',
          }}
        >
          <Sparkles size={24} />
        </div>

        {/* Text */}
        <div style={{ flex: 1, paddingLeft: '20px' }}>
          <h4
            style={{
              margin: '0 0 6px 0',
              color: isLight ? '#065f46' : '#ffffff',
              fontSize: '15px',
              fontWeight: 700,
              textAlign: 'right',
            }}
          >
            تحديث جديد متوفر ✨
          </h4>
          
          <p
            style={{
              margin: '0 0 12.5px 0',
              color: isLight ? '#047857' : '#a7f3d0',
              fontSize: '12.5px',
              lineHeight: '1.6',
              textAlign: 'right',
            }}
          >
            تتوفر إصلاحات وتحسينات جديدة على النظام الأساسي. اضغط على الزر أدناه لتحديث التطبيق مباشرةً دون الحاجة لإعادة التثبيت.
          </p>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start' }}>
            <button
              onClick={handleUpdate}
              disabled={isUpdating}
              style={{
                background: 'linear-gradient(90deg, #10b981, #059669)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
              }}
            >
              {isUpdating ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  <span>جاري التحديث...</span>
                </>
              ) : (
                <>
                  <RefreshCw size={14} />
                  <span>تحديث الآن</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => setHasUpdate(false)}
              disabled={isUpdating}
              style={{
                background: 'transparent',
                border: 'none',
                color: isLight ? '#047857' : '#34d399',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                padding: '8px 12px',
                transition: 'color 0.2s',
              }}
            >
              لاحقاً
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
